import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import Mandala from "../components/decor/Mandala";
import { toast } from "sonner";
import {
  CalendarDays,
  Clock,
  BedDouble,
  Users,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Printer,
  Copy,
} from "lucide-react";
import { ROOM_TYPES, REFUNDABLE_DEPOSIT, BOOKING_RULES } from "../data/rooms";
import { API_CONFIG } from "../api/config";
import { IMG } from "../data/site";
import { useLang } from "../i18n/LanguageContext";

const STORAGE_KEY = "sode_room_bookings_v1";
const STAY_HOURS = 24;
const checkInInputClass =
  "block w-full min-w-0 max-w-full box-border appearance-none rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-2.5 sm:px-4 py-3 text-center text-base sm:text-lg text-[#3B2A1A] leading-tight focus:outline-none focus:ring-2 focus:ring-[#B65A14] disabled:opacity-60";

const StepPill = ({ n, label, active, done }) => (
  <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
    <div
      className={`w-7 h-7 sm:w-8 sm:h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold border ${
        done
          ? "bg-[#B65A14] text-[#FBF7F0] border-[#B65A14]"
          : active
          ? "bg-[#D4AF37] text-[#3B2A1A] border-[#D4AF37]"
          : "bg-[#FBF7F0] text-[#3B2A1A]/60 border-[#D4AF37]/40"
      }`}
    >
      {done ? <CheckCircle2 className="w-4 h-4" /> : n}
    </div>
    <span className={`min-w-0 text-[11px] sm:text-sm leading-tight ${active || done ? "text-[#3B2A1A] font-semibold" : "text-[#3B2A1A]/60"}`}>
      {label}
    </span>
  </div>
);

const todayISO = () => new Date().toISOString().slice(0, 10);
const toDateInputValue = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const toTimeInputValue = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const laterTime = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};
const clampTimeValue = (value, min, max) => {
  if (!value) return value;
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
};
const currentTimeValue = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const addDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const formatLongDate = (iso, lang = "en") => {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "kn" ? "kn-IN" : "en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};
const buildDateTime = (iso, time = "00:00") => new Date(`${iso}T${time || "00:00"}:00`);
const numbersOnly = (value = "") => value.replace(/\D/g, "");
const addHours = (iso, time, hrs) => {
  const d = buildDateTime(iso, time);
  d.setHours(d.getHours() + hrs);
  return d;
};
const formatDateTime = (date, lang = "en") =>
  date.toLocaleString(lang === "kn" ? "kn-IN" : "en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getRoomAllocationNameSuffix = (roomAllocationNames = []) => {
  const firstName = roomAllocationNames.find(Boolean);
  if (!firstName) return "";
  return String(firstName).trim();
};

const buildBookingId = (orderId, roomAllocationNames = []) => {
  const suffix = getRoomAllocationNameSuffix(roomAllocationNames);
  return suffix ? `${orderId}-${suffix}` : orderId;
};

const genBookingId = () => {
  const s = "SODE-" + Math.random().toString(36).slice(2, 8).toUpperCase();
  return s;
};

const loadBookings = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
};
const saveBookings = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

const COPY = {
  chooseDate: { en: "Choose date", kn: "ದಿನಾಂಕ ಆಯ್ಕೆಮಾಡಿ" },
  selectRoom: { en: "Select room", kn: "ಕೊಠಡಿ ಆಯ್ಕೆಮಾಡಿ" },
  yourDetails: { en: "Your details", kn: "ನಿಮ್ಮ ವಿವರಗಳು" },
  confirmation: { en: "Confirmation", kn: "ದೃಢೀಕರಣ" },
  step1: { en: "Step 1 of 4", kn: "ಹಂತ 1 / 4" },
  step2: { en: "Step 2 of 4", kn: "ಹಂತ 2 / 4" },
  step3: { en: "Step 3 of 4", kn: "ಹಂತ 3 / 4" },
  chooseCheckIn: { en: "Choose your check-in date", kn: "ಚೆಕ್-ಇನ್ ದಿನಾಂಕ ಆಯ್ಕೆಮಾಡಿ" },
  checkInDate: { en: "Check-in date", kn: "ಚೆಕ್-ಇನ್ ದಿನಾಂಕ" },
  checkInTime: { en: "Check-in time", kn: "ಚೆಕ್-ಇನ್ ಸಮಯ" },
  checkoutInfo: { en: "Check-out is 24 hours after check-in. Booking window:", kn: "ಚೆಕ್-ಇನ್ ಆದ 24 ಗಂಟೆಗಳ ನಂತರ ಚೆಕ್-ಔಟ್. ಬುಕಿಂಗ್ ಅವಧಿ:" },
  checkoutPreview: { en: "Check-out", kn: "ಚೆಕ್-ಔಟ್" },
  checkAvailability: { en: "Check Availability", kn: "ಲಭ್ಯತೆ ಪರಿಶೀಲಿಸಿ" },
  checkingAvailability: { en: "Checking availability...", kn: "ಲಭ್ಯತೆ ಪರಿಶೀಲಿಸಲಾಗುತ್ತಿದೆ..." },
  availableRoomsFor: { en: "Available rooms for", kn: "ಲಭ್ಯವಿರುವ ಕೊಠಡಿಗಳು" },
  roomRate: { en: "Room rate", kn: "ಕೊಠಡಿ ದರ" },
  upTo: { en: "Up to", kn: "ಗರಿಷ್ಠ" },
  stay24: { en: "24-hr stay", kn: "24 ಗಂಟೆಗಳ ವಾಸ" },
  per24: { en: "/ 24 hrs", kn: "/ 24 ಗಂಟೆ" },
  soldOut: { en: "Sold out", kn: "ಲಭ್ಯವಿಲ್ಲ" },
  available: { en: "available", kn: "ಲಭ್ಯ" },
  noRooms: { en: "No rooms are available for the selected date and time.", kn: "ಆಯ್ಕೆಮಾಡಿದ ದಿನಾಂಕ ಮತ್ತು ಸಮಯಕ್ಕೆ ಕೊಠಡಿಗಳು ಲಭ್ಯವಿಲ್ಲ." },
  amenities: { en: "Amenities", kn: "ಸೌಲಭ್ಯಗಳು" },
  unavailable: { en: "Unavailable", kn: "ಲಭ್ಯವಿಲ್ಲ" },
  changeDate: { en: "Change date", kn: "ದಿನಾಂಕ ಬದಲಿಸಿ" },
  bookingDetails: { en: "Your booking details", kn: "ನಿಮ್ಮ ಬುಕಿಂಗ್ ವಿವರಗಳು" },
  fullName: { en: "Full name", kn: "ಪೂರ್ಣ ಹೆಸರು" },
  namePlaceholder: { en: "Devotee's name", kn: "ಭಕ್ತರ ಹೆಸರು" },
  email: { en: "Email address", kn: "ಇಮೇಲ್ ವಿಳಾಸ" },
  phone: { en: "Phone number", kn: "ದೂರವಾಣಿ ಸಂಖ್ಯೆ" },
  address: { en: "Address", kn: "ವಿಳಾಸ" },
  addressPlaceholder: { en: "Street, city, state", kn: "ರಸ್ತೆ, ನಗರ, ರಾಜ್ಯ" },
  paymentMode: { en: "Payment mode", kn: "ಪಾವತಿ ವಿಧಾನ" },
  choosePaymentMode: { en: "Choose payment mode", kn: "ಪಾವತಿ ವಿಧಾನ ಆಯ್ಕೆಮಾಡಿ" },
  important: { en: "Important - please read", kn: "ಮುಖ್ಯ ಸೂಚನೆ - ದಯವಿಟ್ಟು ಓದಿ" },
  back: { en: "Back", kn: "ಹಿಂದೆ" },
  confirmBooking: { en: "Confirm Booking", kn: "ಬುಕಿಂಗ್ ದೃಢೀಕರಿಸಿ" },
  bookingSummary: { en: "Booking summary", kn: "ಬುಕಿಂಗ್ ಸಾರಾಂಶ" },
  room: { en: "Room", kn: "ಕೊಠಡಿ" },
  checkIn: { en: "Check-in", kn: "ಚೆಕ್-ಇನ್" },
  checkOut: { en: "Check-out", kn: "ಚೆಕ್-ಔಟ್" },
  duration: { en: "Duration", kn: "ಅವಧಿ" },
  depositAtCheckIn: { en: "Deposit (at check-in)", kn: "ಠೇವಣಿ (ಚೆಕ್-ಇನ್ ವೇಳೆ)" },
  payableNow: { en: "Payable now", kn: "ಈಗ ಪಾವತಿಸಬೇಕಾದುದು" },
  depositNote: { en: "A nominal refundable deposit will be collected at the counter during check-in", kn: "ಚೆಕ್-ಇನ್ ಸಮಯದಲ್ಲಿ ಕೌಂಟರ್‌ನಲ್ಲಿ ಅಲ್ಪ ಮೊತ್ತದ, ಮರುಪಾವತಿಸಬಹುದಾದ ಠೇವಣಿಯನ್ನು ಸಂಗ್ರಹಿಸಲಾಗುತ್ತದೆ." },
  bookingConfirmed: { en: "Booking confirmed!", kn: "ಬುಕಿಂಗ್ ದೃಢೀಕರಿಸಲಾಗಿದೆ!" },
  bookingId: { en: "Booking ID", kn: "ಬುಕಿಂಗ್ ಸಂಖ್ಯೆ" },
  copy: { en: "Copy", kn: "ನಕಲಿಸಿ" },
  guest: { en: "Guest", kn: "ಅತಿಥಿ" },
  roomFare: { en: "Room fare", kn: "ಕೊಠಡಿ ದರ" },
  hours24: { en: "24 hours", kn: "24 ಗಂಟೆಗಳು" },
  carryProof: { en: "Please carry a printed or screenshot copy of this confirmation and a valid photo ID. Check-out is exactly 24 hours after check-in - extensions are not allowed.", kn: "ದಯವಿಟ್ಟು ಈ ದೃಢೀಕರಣದ ಮುದ್ರಿತ ಪ್ರತಿಯನ್ನು ಅಥವಾ ಸ್ಕ್ರೀನ್‌ಶಾಟ್ ಅನ್ನು ಹಾಗೂ ಮಾನ್ಯ ಫೋಟೋ ಐಡಿಯನ್ನು ತರಿರಿ. ಚೆಕ್-ಇನ್ ಆದ ನಿಖರವಾಗಿ 24 ಗಂಟೆಗಳ ನಂತರ ಚೆಕ್-ಔಟ್ - ವಿಸ್ತರಣೆಗಳಿಗೆ ಅವಕಾಶವಿಲ್ಲ." },
  printConfirmation: { en: "Print confirmation", kn: "ದೃಢೀಕರಣ ಮುದ್ರಿಸಿ" },
  anotherBooking: { en: "Make another booking", kn: "ಮತ್ತೊಂದು ಬುಕಿಂಗ್ ಮಾಡಿ" },
  dateRangeError: { en: "Please choose a date and time within the allowed booking window.", kn: "ದಯವಿಟ್ಟು ಅನುಮತಿಸಲಾದ ಬುಕಿಂಗ್ ಅವಧಿಯೊಳಗಿನ ದಿನಾಂಕ ಮತ್ತು ಸಮಯವನ್ನು ಆಯ್ಕೆಮಾಡಿ." },
  bookingWindowError: { en: "Unable to load booking date limits. Please try again.", kn: "ಬುಕಿಂಗ್ ದಿನಾಂಕ ಮಿತಿಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." },
  allowedTimeWindow: { en: "Allowed time:", kn: "ಅನುಮತಿಸಲಾದ ಸಮಯ:" },
  timeError: { en: "Please choose a check-in time.", kn: "ದಯವಿಟ್ಟು ಚೆಕ್-ಇನ್ ಸಮಯ ಆಯ್ಕೆಮಾಡಿ." },
  pastTimeError: { en: "Please choose a current or future check-in time.", kn: "ದಯವಿಟ್ಟು ಪ್ರಸ್ತುತ ಅಥವಾ ಮುಂದಿನ ಚೆಕ್-ಇನ್ ಸಮಯ ಆಯ್ಕೆಮಾಡಿ." },
  availabilityError: { en: "Unable to load available rooms. Please try again.", kn: "ಲಭ್ಯವಿರುವ ಕೊಠಡಿಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." },
  nameError: { en: "Please enter your full name.", kn: "ದಯವಿಟ್ಟು ಪೂರ್ಣ ಹೆಸರನ್ನು ನಮೂದಿಸಿ." },
  emailError: { en: "Enter a valid email address.", kn: "ಸರಿಯಾದ ಇಮೇಲ್ ವಿಳಾಸವನ್ನು ನಮೂದಿಸಿ." },
  phoneError: { en: "Enter a valid phone number.", kn: "ಸರಿಯಾದ ದೂರವಾಣಿ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ." },
  addressError: { en: "Please enter your address.", kn: "ದಯವಿಟ್ಟು ನಿಮ್ಮ ವಿಳಾಸ ನಮೂದಿಸಿ." },
  paymentModeError: { en: "Please choose a payment mode.", kn: "ದಯವಿಟ್ಟು ಪಾವತಿ ವಿಧಾನ ಆಯ್ಕೆಮಾಡಿ." },
  paymentChannelsError: { en: "Unable to load payment modes.", kn: "ಪಾವತಿ ವಿಧಾನಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ." },
  takenError: { en: "Sorry, this room was just taken. Please choose another.", kn: "ಕ್ಷಮಿಸಿ, ಈ ಕೊಠಡಿ ಈಗಷ್ಟೇ ಬುಕ್ ಆಗಿದೆ. ದಯವಿಟ್ಟು ಇನ್ನೊಂದು ಆಯ್ಕೆಮಾಡಿ." },
  noRoomReferenceError: { en: "This room cannot be reserved right now. Please choose another.", kn: "ಈ ಕೊಠಡಿಯನ್ನು ಈಗ ಕಾಯ್ದಿರಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ. ದಯವಿಟ್ಟು ಇನ್ನೊಂದು ಆಯ್ಕೆಮಾಡಿ." },
  freezeError: { en: "Unable to freeze this room. Please choose another.", kn: "ಈ ಕೊಠಡಿಯನ್ನು ಫ್ರೀಜ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಇನ್ನೊಂದು ಆಯ್ಕೆಮಾಡಿ." },
  reserveError: { en: "Unable to reserve this room. Please try again.", kn: "ಈ ಕೊಠಡಿಯನ್ನು ಕಾಯ್ದಿರಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." },
  cashfreeMissingError: { en: "Cashfree payment gateway is not available. Please refresh and try again.", kn: "Cashfree ಪಾವತಿ ಗೇಟ್‌ವೇ ಲಭ್ಯವಿಲ್ಲ. ದಯವಿಟ್ಟು ರಿಫ್ರೆಶ್ ಮಾಡಿ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." },
  paymentSessionError: { en: "Payment session was not created. Please try again.", kn: "ಪಾವತಿ ಸೆಷನ್ ಸೃಷ್ಟಿಯಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." },
  paymentFailedError: { en: "Payment was cancelled or failed.", kn: "ಪಾವತಿ ರದ್ದುಗೊಂಡಿದೆ ಅಥವಾ ವಿಫಲವಾಗಿದೆ." },
  paymentRedirected: { en: "Payment redirected. Please complete the payment in the opened window.", kn: "ಪಾವತಿಯನ್ನು ಮರುನಿರ್ದೇಶಿಸಲಾಗಿದೆ. ತೆರೆದ ವಿಂಡೋದಲ್ಲಿ ಪಾವತಿಯನ್ನು ಪೂರ್ಣಗೊಳಿಸಿ." },
  paymentStatusError: { en: "Unable to verify payment status. Please try again.", kn: "ಪಾವತಿ ಸ್ಥಿತಿಯನ್ನು ಪರಿಶೀಲಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ." },
  paymentNotSuccessful: { en: "Payment status:", kn: "ಪಾವತಿ ಸ್ಥಿತಿ:" },
  freezingRoom: { en: "Freezing room...", kn: "ಕೊಠಡಿಯನ್ನು ಫ್ರೀಜ್ ಮಾಡಲಾಗುತ್ತಿದೆ..." },
  reservingRoom: { en: "Starting payment...", kn: "ಪಾವತಿ ಪ್ರಾರಂಭಿಸಲಾಗುತ್ತಿದೆ..." },
  bookingSuccess: { en: "Booking confirmed! ID:", kn: "ಬುಕಿಂಗ್ ದೃಢೀಕರಿಸಲಾಗಿದೆ! ಸಂಖ್ಯೆ:" },
  copied: { en: "Booking ID copied", kn: "ಬುಕಿಂಗ್ ಸಂಖ್ಯೆ ನಕಲಿಸಲಾಗಿದೆ" },
};

const ROOM_COPY = {
  standard: {
    name: { en: "Standard Room", kn: "ಸ್ಟ್ಯಾಂಡರ್ಡ್ ಕೊಠಡಿ" },
    tagline: { en: "Comfortable, essential amenities", kn: "ಆರಾಮದಾಯಕ, ಅಗತ್ಯ ಸೌಲಭ್ಯಗಳು" },
    amenities: [
      { en: "Attached bathroom", kn: "ಲಗತ್ತಿಸಿದ ಸ್ನಾನಗೃಹ" },
      { en: "Cot with mattress", kn: "ಹಾಸಿಗೆಯೊಂದಿಗೆ ಮಂಚ" },
      { en: "Ceiling fan", kn: "ಸೀಲಿಂಗ್ ಫ್ಯಾನ್" },
      { en: "Drinking water", kn: "ಕುಡಿಯುವ ನೀರು" },
      { en: "Bed linen", kn: "ಹಾಸಿಗೆ ಬಟ್ಟೆ" },
    ],
  },
  deluxe: {
    name: { en: "Deluxe Room", kn: "ಡೆಲಕ್ಸ್ ಕೊಠಡಿ" },
    tagline: { en: "Spacious with additional comfort", kn: "ವಿಶಾಲ ಮತ್ತು ಹೆಚ್ಚುವರಿ ಆರಾಮ" },
    amenities: [
      { en: "Attached bathroom with hot water", kn: "ಬಿಸಿ ನೀರಿನೊಂದಿಗೆ ಲಗತ್ತಿಸಿದ ಸ್ನಾನಗೃಹ" },
      { en: "Queen-size bed", kn: "ಕ್ವೀನ್ ಗಾತ್ರದ ಹಾಸಿಗೆ" },
      { en: "Air conditioning", kn: "ಏರ್ ಕಂಡೀಷನಿಂಗ್" },
      { en: "Study desk", kn: "ಅಧ್ಯಯನ ಮೇಜು" },
      { en: "Complimentary tea/coffee", kn: "ಉಚಿತ ಚಹಾ/ಕಾಫಿ" },
    ],
  },
  family: {
    name: { en: "Family Suite", kn: "ಕುಟುಂಬ ಸೂಟ್" },
    tagline: { en: "For families & small groups", kn: "ಕುಟುಂಬಗಳು ಮತ್ತು ಸಣ್ಣ ಗುಂಪುಗಳಿಗೆ" },
    amenities: [
      { en: "Two bedrooms", kn: "ಎರಡು ಮಲಗುವ ಕೊಠಡಿಗಳು" },
      { en: "Living area", kn: "ವಾಸದ ಪ್ರದೇಶ" },
      { en: "Attached bathroom (hot water)", kn: "ಲಗತ್ತಿಸಿದ ಸ್ನಾನಗೃಹ (ಬಿಸಿ ನೀರು)" },
      { en: "Air conditioning", kn: "ಏರ್ ಕಂಡೀಷನಿಂಗ್" },
      { en: "Refrigerator & tea maker", kn: "ಫ್ರಿಜ್ ಮತ್ತು ಚಹಾ ತಯಾರಕ" },
    ],
  },
};

const RULES_KN = [
  "ಬುಕಿಂಗ್ ಒಂದು ದಿನಕ್ಕೆ ಮಾತ್ರ (ಚೆಕ್-ಇನ್‌ನಿಂದ 24 ಗಂಟೆಗಳು).",
  "ಬುಕಿಂಗ್ ನಂತರ ರದ್ದು, ಮರುಪಾವತಿ ಅಥವಾ ದಿನಾಂಕ / ಸಮಯ ಬದಲಾವಣೆ ಅನುಮತಿಸಲಾಗುವುದಿಲ್ಲ.",
  "ವಾಸದ ವಿಸ್ತರಣೆ ಅನುಮತಿಸಲಾಗುವುದಿಲ್ಲ. ಚೆಕ್-ಔಟ್ ಸಮಯ ಕಟ್ಟುನಿಟ್ಟಾಗಿದೆ.",
  "ಒಬ್ಬ ಬಳಕೆದಾರರಿಗೆ (ಇಮೇಲ್) ತಿಂಗಳಿಗೆ ಗರಿಷ್ಠ ಎರಡು ಕೊಠಡಿ ಬುಕಿಂಗ್‌ಗಳು.",
  "ಬುಕಿಂಗ್ ಅವಧಿ: ಲಭ್ಯವಿರುವ ದಿನಾಂಕ ಆಯ್ಕೆ ಮಿತಿಯೊಳಗೆ ಮಾತ್ರ.",
  "ಚೆಕ್-ಇನ್ ಸಮಯದಲ್ಲಿ ಕೌಂಟರ್‌ನಲ್ಲಿ ಅಲ್ಪ ಮೊತ್ತದ, ಮರುಪಾವತಿಸಬಹುದಾದ ಠೇವಣಿಯನ್ನು ಸಂಗ್ರಹಿಸಲಾಗುತ್ತದೆ.",
  "ನೀತಿಯ ಅವಶ್ಯಕತೆ ಇದ್ದಲ್ಲಿ ಶ್ರೀ ಸೋದೆ ವಾದಿರಾಜ ಮಠವು ಬುಕಿಂಗ್‌ಗಳನ್ನು ರದ್ದುಪಡಿಸಿ ಮರುಪಾವತಿಸಬಹುದು.",
];

const normalizeRoomConfiguration = (room, index) => {
  const fallback = ROOM_TYPES[index % ROOM_TYPES.length] || ROOM_TYPES[0];
  const amenities = Array.isArray(room.amenities)
    ? room.amenities.map((amenity) => (typeof amenity === "string" ? amenity : amenity?.name)).filter(Boolean)
    : [];

  return {
    ...room,
    id: String(room.floorRoomTypeMapId || room.roomTypeId || index),
    name: room.roomType || fallback.name,
    tagline: room.buildingName ? `${room.buildingName}${room.floorNumber ? `, Floor ${room.floorNumber}` : ""}` : fallback.tagline,
    price: Number(room.amount || 0),
    capacity: room.occupancy || fallback.capacity,
    amenities,
    descriptionHtml: room.description || "",
    remaining: Number(room.availableCount || 0),
    image: fallback.image,
  };
};

const getRoomApiBaseUrl = () => {
  return API_CONFIG.roomApiBaseUrl?.replace(/\/$/, "");
};

const postRoomJson = async (path, body) => {
  const response = await fetch(`${getRoomApiBaseUrl()}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "*/*",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Room request failed with ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.status !== 1) {
    throw new Error(payload?.error?.message || "Room request failed.");
  }

  return payload?.data;
};

const extractRoomAllocationReference = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (data.roomAllocationReference) return data.roomAllocationReference;
  if (Array.isArray(data)) return data.find((item) => item?.roomAllocationReference)?.roomAllocationReference || "";
  return data.reservationReferences?.[0]?.roomAllocationReference || data.roomAllocations?.[0]?.roomAllocationReference || "";
};

const extractRoomAllocationNames = (data) => {
  if (!data || typeof data === "string") return [];
  if (Array.isArray(data.roomAllocationNames)) return data.roomAllocationNames.filter(Boolean).map(String);
  if (Array.isArray(data.roomNames)) return data.roomNames.filter(Boolean).map(String);
  if (data.roomAllocationName) return [String(data.roomAllocationName)];
  if (data.roomName) return [String(data.roomName)];
  if (Array.isArray(data)) {
    return data
      .map((item) => item?.roomAllocationName || item?.roomAllocationNames?.[0] || item?.roomName || item?.name)
      .filter(Boolean)
      .map(String);
  }
  return [
    ...(data.reservationReferences || []),
    ...(data.roomAllocations || []),
  ]
    .map((item) => item?.roomAllocationName || item?.roomAllocationNames?.[0] || item?.roomName || item?.name)
    .filter(Boolean)
    .map(String);
};

const fetchRoomPaymentChannels = async () => {
  const response = await fetch(`${getRoomApiBaseUrl()}/payment-channels`, {
    headers: { accept: "*/*" },
  });

  if (!response.ok) {
    throw new Error(`Payment channels request failed with ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.status !== 1) {
    throw new Error(payload?.error?.message || "Unable to load payment channels.");
  }

  return (payload?.data || [])
    .map((channel) => ({
      id: String(channel.paymentChannelId),
      name: channel.name,
      label: channel.description || channel.name,
    }))
    .filter((channel) => {
      const text = `${channel.name || ""} ${channel.label || ""}`.toLowerCase();
      return !text.includes("bank account si");
    });
};

const fetchRoomBookingConfig = async () => {
  const params = new URLSearchParams({ institutionReference: API_CONFIG.roomInstitutionReference });
  const response = await fetch(`${getRoomApiBaseUrl()}/room-booking-config?${params.toString()}`, {
    headers: { accept: "*/*" },
  });

  if (!response.ok) {
    throw new Error(`Room booking config request failed with ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.status !== 1) {
    throw new Error(payload?.error?.message || "Unable to load booking date limits.");
  }

  const allowedBookingFrom = Number(payload?.data?.allowedBookingFrom);
  const allowedBookingTill = Number(payload?.data?.allowedBookingTill);
  if (!Number.isFinite(allowedBookingFrom) || !Number.isFinite(allowedBookingTill)) {
    throw new Error("Room booking config response did not include valid booking limits.");
  }

  return { allowedBookingFrom, allowedBookingTill };
};

const fetchAvailableRooms = async ({ checkInDate, checkInTime }) => {
  const checkIn = buildDateTime(checkInDate, checkInTime);
  const checkOut = addHours(checkInDate, checkInTime, STAY_HOURS);
  const data = await postRoomJson("/available-room-configurations", {
    institutionReference: API_CONFIG.roomInstitutionReference,
    checkInDate: checkIn.getTime(),
    checkOutDate: checkOut.getTime(),
  });

  return (data || []).map(normalizeRoomConfiguration);
};

const freezeRoom = async ({ room, checkInDate, checkInTime }) => {
  const firstRoomReference = room.roomReferences?.[0];
  if (!firstRoomReference) {
    throw new Error("No room reference available.");
  }

  const checkIn = buildDateTime(checkInDate, checkInTime);
  const checkOut = addHours(checkInDate, checkInTime, STAY_HOURS);
  const data = await postRoomJson("/freeze-room", {
    roomReference: [firstRoomReference],
    checkInDate: checkIn.getTime(),
    checkOutDate: checkOut.getTime(),
    numberOfPerson: room.capacity,
    arrivalTime: checkInTime,
  });
  const roomAllocationReference = extractRoomAllocationReference(data);
  const roomAllocationNames = extractRoomAllocationNames(data);

  if (!roomAllocationReference) {
    throw new Error("Freeze response did not include a room allocation reference.");
  }

  return {
    roomReference: firstRoomReference,
    roomAllocationReference,
    roomAllocationNames: roomAllocationNames.length ? roomAllocationNames : extractRoomAllocationNames(room),
    rawFreezeResponse: data,
  };
};

const reserveRoom = async ({ form, paymentChannelId, roomAllocationReference, roomAllocationNames = [] }) => {
  return postRoomJson("/reserve-room", {
    phone: numbersOnly(form.phone),
    address: form.address.trim(),
    devoteeNameDup: null,
    devoteeName: form.name.trim(),
    emailAddress: form.email.trim(),
    paymentChannelId: String(paymentChannelId),
    institutionReference: API_CONFIG.roomInstitutionReference,
    parentAllocationReference: null,
    reservationReferences: [
      {
        roomAllocationReference,
        isMasterRoomAllocation: true,
      },
    ],
    roomAllocationNames: roomAllocationNames.filter(Boolean).map(String),
  });
};

const fetchRoomPaymentStatus = async (orderId) => {
  const params = new URLSearchParams({ orderId });
  const response = await fetch(`${getRoomApiBaseUrl()}/room-reservation/payment-status?${params.toString()}`, {
    headers: { accept: "*/*" },
  });

  if (!response.ok) {
    throw new Error(`Payment status request failed with ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.status !== 1) {
    throw new Error(payload?.error?.message || "Unable to verify payment status.");
  }

  return payload?.data;
};

const getStatusText = (statusPayload) => {
  if (!statusPayload) return "";
  if (typeof statusPayload === "string") return statusPayload;
  return statusPayload.status || statusPayload.paymentStatus || statusPayload.transactionStatus || "";
};

const isSuccessfulPaymentStatus = (status) =>
  ["SUCCESS", "PAID", "COMPLETED", "CAPTURED"].includes(String(status || "").toUpperCase());

const RoomBooking = () => {
  const { t, lang } = useLang();
  const L = (copy) => (copy && copy[lang]) || copy.en;
  const roomText = (room, key) => ROOM_COPY[room.id]?.[key]?.[lang] || ROOM_COPY[room.id]?.[key]?.en || room[key];
  const roomAmenities = (room) => ROOM_COPY[room.id]?.amenities?.map((item) => L(item)) || room.amenities;
  const rules = lang === "kn"
    ? RULES_KN
    : BOOKING_RULES.map((rule) =>
        rule
          .replace("23 hours", "24 hours")
          .replace("Booking window: today to 30 days in advance.", "Booking window: only within the available date limits.")
      );
  const [step, setStep] = useState("date"); // date | rooms | form | confirmed
  const [checkInDate, setCheckInDate] = useState(addDaysISO(0));
  const [checkInTime, setCheckInTime] = useState(currentTimeValue);
  const [availability, setAvailability] = useState(null);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [freezingRoomId, setFreezingRoomId] = useState("");
  const [bookingConfig, setBookingConfig] = useState(null);
  const [bookingConfigLoading, setBookingConfigLoading] = useState(true);
  const [paymentChannels, setPaymentChannels] = useState([]);
  const [paymentChannelsLoading, setPaymentChannelsLoading] = useState(true);
  const [isReservingRoom, setIsReservingRoom] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", paymentMode: "" });
  const [errors, setErrors] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const activeStepRef = useRef(null);

  const minDate = useMemo(
    () => (bookingConfig ? toDateInputValue(bookingConfig.allowedBookingFrom) : addDaysISO(0)),
    [bookingConfig]
  );
  const maxDate = useMemo(
    () => (bookingConfig ? toDateInputValue(bookingConfig.allowedBookingTill) : addDaysISO(30)),
    [bookingConfig]
  );
  const checkOutDateTime = useMemo(
    () => addHours(checkInDate, checkInTime, STAY_HOURS),
    [checkInDate, checkInTime]
  );
  const minimumCheckInTime = useMemo(() => {
    const todayMinimum = checkInDate === todayISO() ? currentTimeValue() : undefined;
    const configMinimum = bookingConfig && checkInDate === minDate ? toTimeInputValue(bookingConfig.allowedBookingFrom) : undefined;
    return laterTime(todayMinimum, configMinimum);
  }, [bookingConfig, checkInDate, minDate]);
  const maximumCheckInTime = useMemo(
    () => (bookingConfig && checkInDate === maxDate ? toTimeInputValue(bookingConfig.allowedBookingTill) : undefined),
    [bookingConfig, checkInDate, maxDate]
  );
  const selectedCheckInTimestamp = useMemo(
    () => (checkInDate && checkInTime ? buildDateTime(checkInDate, checkInTime).getTime() : null),
    [checkInDate, checkInTime]
  );
  const isCheckInOutsideBookingWindow = Boolean(
    bookingConfig &&
      selectedCheckInTimestamp &&
      (selectedCheckInTimestamp < bookingConfig.allowedBookingFrom || selectedCheckInTimestamp > bookingConfig.allowedBookingTill)
  );
  const timeWindowText = useMemo(() => {
    if (!minimumCheckInTime && !maximumCheckInTime) return "";
    return `${minimumCheckInTime || "00:00"} - ${maximumCheckInTime || "23:59"}`;
  }, [maximumCheckInTime, minimumCheckInTime]);

  useEffect(() => {
    let alive = true;

    const loadPaymentChannels = async () => {
      try {
        const channels = await fetchRoomPaymentChannels();
        if (!alive) return;
        setPaymentChannels(channels);
      } catch (error) {
        console.error(error);
        if (alive) toast.error(COPY.paymentChannelsError[lang] || COPY.paymentChannelsError.en);
      } finally {
        if (alive) setPaymentChannelsLoading(false);
      }
    };

    loadPaymentChannels();

    return () => { alive = false; };
  }, [lang]);

  useEffect(() => {
    let alive = true;

    const loadBookingConfig = async () => {
      setBookingConfigLoading(true);
      try {
        const config = await fetchRoomBookingConfig();
        if (!alive) return;
        setBookingConfig(config);
      } catch (error) {
        console.error(error);
        if (alive) toast.error(COPY.bookingWindowError[lang] || COPY.bookingWindowError.en);
      } finally {
        if (alive) setBookingConfigLoading(false);
      }
    };

    loadBookingConfig();

    return () => { alive = false; };
  }, [lang]);

  useEffect(() => {
    if (!bookingConfig) return;
    if (checkInDate < minDate || checkInDate > maxDate) {
      setCheckInDate(minDate);
    }
  }, [bookingConfig, checkInDate, minDate, maxDate]);

  useEffect(() => {
    if (!checkInTime) return;
    if (minimumCheckInTime && checkInTime < minimumCheckInTime) {
      setCheckInTime(minimumCheckInTime);
      return;
    }
    if (maximumCheckInTime && checkInTime > maximumCheckInTime) {
      setCheckInTime(maximumCheckInTime);
    }
  }, [checkInTime, maximumCheckInTime, minimumCheckInTime]);

  useEffect(() => {
    if (step === "date") return;

    const frame = window.requestAnimationFrame(() => {
      activeStepRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [step]);

  const checkAvailability = async () => {
    if (!checkInDate) return;
    if (checkInDate < minDate || checkInDate > maxDate) {
      toast.error(L(COPY.dateRangeError));
      return;
    }
    if (!checkInTime) {
      toast.error(L(COPY.timeError));
      return;
    }
    if (selectedCheckInTimestamp < Date.now()) {
      toast.error(L(COPY.pastTimeError));
      return;
    }
    if (isCheckInOutsideBookingWindow) {
      toast.error(L(COPY.dateRangeError));
      return;
    }

    setIsCheckingAvailability(true);
    setSelectedRoom(null);
    try {
      const list = await fetchAvailableRooms({ checkInDate, checkInTime });
      setAvailability(list);
      setStep("rooms");
    } catch (error) {
      console.error(error);
      toast.error(L(COPY.availabilityError));
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const pickRoom = async (room) => {
    if (room.remaining <= 0) return;
    if (!room.roomReferences?.[0]) {
      toast.error(L(COPY.noRoomReferenceError));
      return;
    }

    setFreezingRoomId(room.id);
    try {
      const freezeDetails = await freezeRoom({ room, checkInDate, checkInTime });
      setSelectedRoom({ ...room, ...freezeDetails });
      setErrors({});
      setStep("form");
    } catch (error) {
      console.error(error);
      toast.error(L(COPY.freezeError));
    } finally {
      setFreezingRoomId("");
    }
  };

  const validateForm = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 3) e.name = L(COPY.nameError);
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = L(COPY.emailError);
    if (!/^[+\d][\d\s-]{7,15}$/.test(form.phone)) e.phone = L(COPY.phoneError);
    if (!form.address.trim()) e.address = L(COPY.addressError);
    if (!form.paymentMode) e.paymentMode = L(COPY.paymentModeError);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const confirmBooking = async () => {
    if (!validateForm()) return;
    const emailKey = form.email.trim().toLowerCase();
    const bookings = loadBookings();
    if (!selectedRoom || selectedRoom.remaining <= 0) {
      toast.error(L(COPY.takenError));
      return;
    }
    if (!selectedRoom.roomAllocationReference) {
      toast.error(L(COPY.freezeError));
      return;
    }

    const bookingId = genBookingId();
    const checkInDateTime = buildDateTime(checkInDate, checkInTime);
    const checkOutDate = addHours(checkInDate, checkInTime, STAY_HOURS);
    setIsReservingRoom(true);
    try {
      const reservation = await reserveRoom({
        form,
        paymentChannelId: form.paymentMode,
        roomAllocationReference: selectedRoom.roomAllocationReference[0],
        roomAllocationNames: selectedRoom.roomAllocationNames,
      });
      if (!reservation?.paymentSessionId) {
        throw new Error(L(COPY.paymentSessionError));
      }

      if (!window.Cashfree) {
        toast.error(L(COPY.cashfreeMissingError));
        return;
      }

      const cashfree = window.Cashfree({ mode: API_CONFIG.roomCashfreeMode });
      const paymentResult = await cashfree.checkout({
        paymentSessionId: reservation.paymentSessionId,
        redirectTarget: "_modal",
      });

      if (paymentResult?.error) {
        toast.error(L(COPY.paymentFailedError));
        return;
      }

      if (paymentResult?.redirect) {
        toast.info(L(COPY.paymentRedirected));
        return;
      }

      if (!reservation.orderId) {
        throw new Error(L(COPY.paymentStatusError));
      }

      const paymentStatusPayload = await fetchRoomPaymentStatus(reservation.orderId);
      const paymentStatus = getStatusText(paymentStatusPayload);
      if (!isSuccessfulPaymentStatus(paymentStatus)) {
        toast.error(`${L(COPY.paymentNotSuccessful)} ${paymentStatus || "PENDING"}`);
        return;
      }

      const roomAllocationNames = reservation.roomAllocationNames || selectedRoom.roomAllocationNames || [];
      const orderId = reservation.orderId || bookingId;
      const record = {
        bookingId: buildBookingId(orderId, roomAllocationNames),
        name: form.name.trim(),
        email: emailKey,
        phone: numbersOnly(form.phone),
        address: form.address.trim(),
        paymentChannelId: form.paymentMode,
        paymentChannelName: paymentChannels.find((channel) => String(channel.id) === String(form.paymentMode))?.label || "",
        roomTypeId: selectedRoom.id,
        roomTypeName: roomText(selectedRoom, "name"),
        roomReference: selectedRoom.roomReference,
        roomAllocationReference: selectedRoom.roomAllocationReference,
        roomAllocationNames,
        parentAllocationReference: reservation.parentAllocationReference || null,
        orderId,
        transactionId: reservation.transactionId || "",
        cashfreeReferenceId: reservation.cashfreeReferenceId || "",
        paymentSessionId: reservation.paymentSessionId,
        paymentStatus,
        paymentStatusDetails: paymentStatusPayload,
        paidAt: new Date().toISOString(),
        reservation,
        price: selectedRoom.price,
        checkInDate,
        checkInTime,
        checkInISO: checkInDateTime.toISOString(),
        checkOutISO: checkOutDate.toISOString(),
        createdAt: new Date().toISOString(),
      };
      saveBookings([...bookings, record]);
      setConfirmation(record);
      setStep("confirmed");
      toast.success(`${L(COPY.bookingSuccess)} ${record.bookingId}`);
    } catch (error) {
      console.error(error);
      toast.error(error.message || L(COPY.reserveError));
    } finally {
      setIsReservingRoom(false);
    }
  };

  const restart = useCallback(() => {
    setStep("date");
    setAvailability(null);
    setSelectedRoom(null);
    setForm({ name: "", email: "", phone: "", address: "", paymentMode: "" });
    setErrors({});
    setConfirmation(null);
  }, []);

  useEffect(() => {
    window.addEventListener("sode:restart-room-booking", restart);
    return () => window.removeEventListener("sode:restart-room-booking", restart);
  }, [restart]);

  return (
    <PageLayout>
      <PageHero
        eyebrow={t("rooms.hero.eyebrow")}
        title={t("rooms.hero.title")}
        subtitle={t("rooms.hero.subtitle")}
        image={IMG.templeCorridor}
      />

      {/* Stepper */}
      <section className="py-3 sm:py-8 bg-[#FBF7F0]/80 border-b border-[#D4AF37]/20 sticky top-0 z-20 backdrop-blur-sm" data-testid="booking-stepper">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 lg:px-8 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-3 sm:gap-6">
          <StepPill n="1" label={L(COPY.chooseDate)} active={step === "date"} done={["rooms", "form", "confirmed"].includes(step)} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="2" label={L(COPY.selectRoom)} active={step === "rooms"} done={["form", "confirmed"].includes(step)} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="3" label={L(COPY.yourDetails)} active={step === "form"} done={step === "confirmed"} />
          <div className="hidden sm:block w-6 h-px bg-[#D4AF37]/40" />
          <StepPill n="4" label={L(COPY.confirmation)} active={step === "confirmed"} done={step === "confirmed"} />
        </div>
      </section>

      {/* STEP 1: Date */}
      {step === "date" && (
        <section className="py-16 sm:py-20" data-testid="step-date">
          <div className="max-w-3xl mx-auto px-4 lg:px-8">
            <SectionHeading eyebrow={L(COPY.step1)} title={L(COPY.chooseCheckIn)} align="center" />
            <div className="mt-10 min-w-0 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-4 sm:p-8 shadow-lg relative overflow-hidden">
              <div className="absolute -top-6 -right-6 pointer-events-none">
                <Mandala size={140} color="#D4AF37" opacity={0.35} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4 min-w-0">
                <div className="min-w-0">
                  <label className="block text-sm font-semibold text-[#3B2A1A] mb-2">
                    <CalendarDays className="inline w-4 h-4 mr-2 -mt-1 text-[#B65A14]" />
                    {L(COPY.checkInDate)}
                  </label>
                  <input
                    type="date"
                    min={minDate}
                    max={maxDate}
                    value={checkInDate}
                    disabled={bookingConfigLoading}
                    onChange={(e) => setCheckInDate(e.target.value)}
                    className={checkInInputClass}
                    style={{ minWidth: 0, WebkitAppearance: "none" }}
                    data-testid="checkin-input"
                  />
                </div>
                <div className="min-w-0">
                  <label className="block text-sm font-semibold text-[#3B2A1A] mb-2">
                    <Clock className="inline w-4 h-4 mr-2 -mt-1 text-[#B65A14]" />
                    {L(COPY.checkInTime)}
                  </label>
                  <input
                    type="time"
                    value={checkInTime}
                    min={minimumCheckInTime}
                    max={maximumCheckInTime}
                    onChange={(e) => setCheckInTime(clampTimeValue(e.target.value, minimumCheckInTime, maximumCheckInTime))}
                    className={checkInInputClass}
                    style={{ minWidth: 0, WebkitAppearance: "none" }}
                    data-testid="checkin-time-input"
                  />
                  {/* {timeWindowText && (
                    <p className="mt-1 text-xs text-[#3B2A1A]/65">
                      {L(COPY.allowedTimeWindow)} {timeWindowText}
                    </p>
                  )} */}
                </div>
              </div>
              <p className="mt-2 text-xs text-[#3B2A1A]/70">
                {bookingConfigLoading
                  ? L(COPY.checkingAvailability)
                  : `${L(COPY.checkoutInfo)} ${formatLongDate(minDate, lang)} - ${formatLongDate(maxDate, lang)}.`}
              </p>
              <div className="mt-3 min-w-0 rounded-xl border border-[#D4AF37]/30 bg-[#F2E7D5]/60 px-4 py-3 text-sm text-[#3B2A1A] break-words">
                <span className="text-[#3B2A1A]/70">{L(COPY.checkoutPreview)}: </span>
                <span className="font-semibold">{formatDateTime(checkOutDateTime, lang)}</span>
              </div>

              <div className="mt-6 min-w-0 p-4 rounded-xl bg-[#F2E7D5]/70 border border-[#D4AF37]/30">
                <div className="flex items-start gap-2 text-xs text-[#3B2A1A]/85">
                  <AlertCircle className="w-4 h-4 text-[#B65A14] mt-0.5 shrink-0" />
                  <ul className="space-y-1 list-disc list-inside">
                    {rules.slice(0, 4).map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <button
                onClick={checkAvailability}
                disabled={isCheckingAvailability || bookingConfigLoading || isCheckInOutsideBookingWindow}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3.5 text-sm font-semibold hover:bg-[#8F4610] disabled:bg-[#B65A14]/45 disabled:cursor-not-allowed transition shadow"
                data-testid="check-availability-btn"
              >
                {isCheckingAvailability ? L(COPY.checkingAvailability) : L(COPY.checkAvailability)} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* STEP 2: Rooms */}
      {step === "rooms" && availability && (
        <section ref={activeStepRef} className="py-16 sm:py-20 scroll-mt-36 sm:scroll-mt-40" data-testid="step-rooms">
          <div className="max-w-6xl mx-auto px-4 lg:px-8">
            <SectionHeading eyebrow={L(COPY.step2)} title={`${L(COPY.availableRoomsFor)} ${formatDateTime(buildDateTime(checkInDate, checkInTime), lang)}`} align="center" />
            {availability.length === 0 ? (
              <div className="mt-10 rounded-2xl border border-[#D4AF37]/30 bg-[#FBF7F0] p-8 text-center text-[#3B2A1A]/80">
                {L(COPY.noRooms)}
              </div>
            ) : (
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {availability.map((r) => {
                const soldOut = r.remaining <= 0;
                const amenities = roomAmenities(r);
                return (
                  <div key={r.id} className="rounded-2xl overflow-hidden border border-[#D4AF37]/30 bg-[#FBF7F0] card-lift flex flex-col" data-testid={`room-card-${r.id}`}>
                    {/* <div className="aspect-[16/10] overflow-hidden">
                      <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
                    </div> */}
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="font-serif-display text-2xl text-[#3B2A1A]">{roomText(r, "name")}</div>
                      {/* <div className="text-sm text-[#B65A14] italic">{roomText(r, "tagline")}</div> */}
                      <div className="flex items-center gap-3 mt-3 text-xs text-[#3B2A1A]/75">
                        <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {L(COPY.upTo)} {r.capacity}</span>
                        <span className="inline-flex items-center gap-1"><BedDouble className="w-3.5 h-3.5" /> {L(COPY.stay24)}</span>
                      </div>
                      {r.descriptionHtml ? (
                        <div
                          className="room-description mt-4"
                          dangerouslySetInnerHTML={{ __html: r.descriptionHtml }}
                        />
                      ) : amenities.length === 0 ? (
                        <ul className="mt-4 space-y-1 text-sm text-[#3B2A1A]/85 list-disc list-inside">
                          {amenities.map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      ) : null}
                      {amenities.length > 0 && (
                        <div className="mt-4">
                          <div className="text-xs uppercase tracking-widest text-[#3B2A1A]/60">{L(COPY.amenities)}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {amenities.map((amenity) => (
                              <span
                                key={amenity}
                                className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/40 bg-[#F2E7D5]/70 px-2.5 py-1 text-xs font-medium text-[#3B2A1A]/85"
                              >
                                <CheckCircle2 className="w-3 h-3 text-[#B65A14]" />
                                {amenity}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="mt-5 flex items-end justify-between">
                        <div>
                          <div className="text-xs uppercase tracking-widest text-[#3B2A1A]/60">{L(COPY.roomRate)}</div>
                          <div className="font-serif-display text-2xl text-[#3B2A1A] flex items-center">
                            <IndianRupee className="w-5 h-5" />{r.price}
                            <span className="text-xs font-normal text-[#3B2A1A]/70 ml-1">{L(COPY.per24)}</span>
                          </div>
                        </div>
                        {/* <div className={`text-xs font-semibold px-3 py-1 rounded-full ${soldOut ? "bg-[#A81F1F]/15 text-[#A81F1F]" : "bg-[#D4AF37]/25 text-[#8F4610]"}`}>
                          {soldOut ? L(COPY.soldOut) : `${r.remaining} ${L(COPY.available)}`}
                        </div> */}
                      </div>
                      <button
                        disabled={soldOut || freezingRoomId === r.id}
                        onClick={() => pickRoom(r)}
                        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition ${
                          soldOut
                            ? "bg-[#F2E7D5] text-[#3B2A1A]/40 cursor-not-allowed"
                            : "bg-[#B65A14] text-[#FBF7F0] hover:bg-[#8F4610]"
                        }`}
                        data-testid={`select-room-${r.id}`}
                      >
                        {soldOut
                          ? L(COPY.unavailable)
                          : freezingRoomId === r.id
                          ? L(COPY.freezingRoom)
                          : (<>{L(COPY.selectRoom)} <ArrowRight className="w-4 h-4" /></>)}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            <div className="mt-8 text-center">
              <button
                onClick={() => setStep("date")}
                className="text-sm text-[#B65A14] hover:text-[#8F4610] underline"
                data-testid="change-date-btn"
              >
                ← {L(COPY.changeDate)}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* STEP 3: Details form */}
      {step === "form" && selectedRoom && (
        <section ref={activeStepRef} className="py-16 sm:py-20 scroll-mt-36 sm:scroll-mt-40" data-testid="step-form">
          <div className="max-w-4xl mx-auto px-4 lg:px-8">
            <SectionHeading eyebrow={L(COPY.step3)} title={L(COPY.bookingDetails)} align="center" />
            <div className="mt-10 grid md:grid-cols-5 gap-6 min-w-0">
              <div className="min-w-0 md:col-span-3 bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-4 sm:p-8 overflow-hidden">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">
                      {L(COPY.fullName)} <span className="text-[#A81F1F]">*</span>
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 block w-full max-w-full min-w-0 box-border rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-3 sm:px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                      placeholder={L(COPY.namePlaceholder)}
                      data-testid="name-input"
                      maxLength={50}
                      required
                    />
                    {errors.name && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-name">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.email)} <span className="text-[#A81F1F]">*</span></label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 block w-full max-w-full min-w-0 box-border rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-3 sm:px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                      placeholder="name@example.com"
                      data-testid="email-input"
                      maxLength={100}
                      required
                    />
                    {errors.email && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-email">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">
                      {L(COPY.phone)} <span className="text-[#A81F1F]">*</span>
                    </label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-1 block w-full max-w-full min-w-0 box-border rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-3 sm:px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                      placeholder="98xxxxxxxx"
                      data-testid="phone-input"
                      maxLength={10}
                      required
                    />
                    {errors.phone && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-phone">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">{L(COPY.address)} <span className="text-[#A81F1F]">*</span></label>
                    <textarea
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full max-w-full min-w-0 box-border rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-3 sm:px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14]"
                      placeholder={L(COPY.addressPlaceholder)}
                      data-testid="address-input"
                      maxLength={200}
                      required
                    />
                    {errors.address && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-address">{errors.address}</p>}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#3B2A1A]">
                      {L(COPY.paymentMode)} <span className="text-[#A81F1F]">*</span>
                    </label>
                    <select
                      value={form.paymentMode}
                      onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}
                      disabled={paymentChannelsLoading}
                      className="mt-1 block w-full max-w-full min-w-0 box-border rounded-xl border border-[#D4AF37]/50 bg-[#FBF7F0] px-3 sm:px-4 py-3 text-[#3B2A1A] focus:outline-none focus:ring-2 focus:ring-[#B65A14] disabled:opacity-60"
                      data-testid="payment-mode-select"
                      required
                    >
                      <option value="">{paymentChannelsLoading ? L(COPY.checkingAvailability) : L(COPY.choosePaymentMode)}</option>
                      {paymentChannels.map((channel) => (
                        <option key={channel.id} value={channel.id}>{channel.label}</option>
                      ))}
                    </select>
                    {errors.paymentMode && <p className="text-xs text-[#A81F1F] mt-1" data-testid="err-paymentMode">{errors.paymentMode}</p>}
                  </div>
                </div>

                <div className="mt-6 p-4 rounded-xl bg-[#F2E7D5]/70 border border-[#D4AF37]/30">
                  <div className="overline text-[#B65A14] mb-2">{L(COPY.important)}</div>
                  <ul className="text-xs text-[#3B2A1A]/85 space-y-1 list-disc list-inside">
                    {rules.map((r) => (<li key={r}>{r}</li>))}
                  </ul>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => setStep("rooms")}
                    className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-6 py-3 text-sm font-semibold hover:bg-[#F2E7D5] transition"
                    data-testid="back-to-rooms-btn"
                  >
                    ← {L(COPY.back)}
                  </button>
                  <button
                    onClick={confirmBooking}
                    disabled={isReservingRoom}
                    className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-7 py-3 text-sm font-semibold hover:bg-[#8F4610] disabled:bg-[#B65A14]/45 disabled:cursor-not-allowed transition shadow"
                    data-testid="confirm-booking-btn"
                  >
                    {isReservingRoom ? L(COPY.reservingRoom) : L(COPY.confirmBooking)} <CheckCircle2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Summary */}
              <aside className="md:col-span-2 bg-gradient-to-b from-[#FBF7F0] to-[#F2E7D5] border border-[#D4AF37]/30 rounded-2xl p-6 h-fit sticky md:top-24" data-testid="booking-summary">
                <div className="font-cinzel text-xs tracking-widest text-[#B65A14]">{L(COPY.bookingSummary)}</div>
                <div className="mt-3 space-y-2 text-sm text-[#3B2A1A]">
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.room)}</span><span className="font-semibold text-right">{roomText(selectedRoom, "name")}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.checkIn)}</span><span className="font-semibold text-right">{formatDateTime(buildDateTime(checkInDate, checkInTime), lang)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.checkOut)}</span><span className="font-semibold text-right">{formatDateTime(checkOutDateTime, lang)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.duration)}</span><span className="font-semibold">{L(COPY.hours24)}</span></div>
                </div>
                <div className="mt-4 border-t border-[#D4AF37]/40 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#3B2A1A]/70">{L(COPY.roomRate)}</span><span className="font-semibold flex items-center"><IndianRupee className="w-3.5 h-3.5" />{selectedRoom.price}</span></div>
                  {/* <div className="flex justify-between gap-3"><span className="text-[#3B2A1A]/70">{L(COPY.depositAtCheckIn)}</span><span className="font-semibold flex items-center"><IndianRupee className="w-3.5 h-3.5" />{REFUNDABLE_DEPOSIT}</span></div> */}
                </div>
                <div className="mt-4 border-t border-[#D4AF37]/40 pt-4 flex justify-between items-baseline">
                  <span className="text-xs uppercase tracking-widest text-[#3B2A1A]/70">{L(COPY.payableNow)}</span>
                  <span className="font-serif-display text-2xl text-[#3B2A1A] flex items-center"><IndianRupee className="w-5 h-5" />{selectedRoom.price}</span>
                </div>
                <p className="mt-3 text-[11px] text-[#3B2A1A]/70">{L(COPY.depositNote)} </p>
              </aside>
            </div>
          </div>
        </section>
      )}

      {/* STEP 4: Confirmation */}
      {step === "confirmed" && confirmation && (
        <section ref={activeStepRef} className="py-16 sm:py-20 scroll-mt-36 sm:scroll-mt-40" data-testid="step-confirmed">
          <div className="max-w-3xl mx-auto px-4 lg:px-8">
            <div className="bg-[#FBF7F0] border border-[#D4AF37]/40 rounded-2xl p-8 relative overflow-hidden shadow-lg">
              <div className="absolute -top-8 -right-8 pointer-events-none">
                <Mandala size={200} color="#D4AF37" opacity={0.35} />
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/30 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-[#8F4610]" />
                </div>
                <div>
                  <div className="overline text-[#B65A14]">{t("site.name")}</div>
                  <h3 className="font-serif-display text-3xl text-[#3B2A1A]">{L(COPY.bookingConfirmed)}</h3>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between bg-[#F2E7D5]/70 border border-[#D4AF37]/40 rounded-xl px-4 py-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#3B2A1A]/70">{L(COPY.bookingId)}</div>
                  <div className="font-cinzel text-lg text-[#3B2A1A]" data-testid="confirmation-id">{confirmation.bookingId}</div>
                </div>
                <button
                  onClick={() => { navigator.clipboard?.writeText(confirmation.bookingId); toast.success(L(COPY.copied)); }}
                  className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-3 py-1.5 text-xs font-semibold hover:bg-[#FBF7F0]"
                  data-testid="copy-id-btn"
                >
                  <Copy className="w-3.5 h-3.5" /> {L(COPY.copy)}
                </button>
              </div>

              <dl className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.guest)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.name}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.email)}</dt><dd className="font-semibold text-[#3B2A1A] break-all">{confirmation.email}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.phone)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.phone}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.paymentMode)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.paymentChannelName}</dd></div>
                <div className="sm:col-span-2"><dt className="text-[#3B2A1A]/70">{L(COPY.address)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.address}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.room)}</dt><dd className="font-semibold text-[#3B2A1A]">{confirmation.roomTypeName}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.checkIn)}</dt><dd className="font-semibold text-[#3B2A1A]">{formatDateTime(new Date(confirmation.checkInISO), lang)}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.checkOut)}</dt><dd className="font-semibold text-[#3B2A1A]">{formatDateTime(new Date(confirmation.checkOutISO), lang)}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.duration)}</dt><dd className="font-semibold text-[#3B2A1A]">{L(COPY.hours24)}</dd></div>
                <div><dt className="text-[#3B2A1A]/70">{L(COPY.roomFare)}</dt><dd className="font-semibold text-[#3B2A1A]">₹{confirmation.price}</dd></div>
              </dl>

              <div className="mt-6 p-4 rounded-xl bg-[#F2E7D5]/70 border border-[#D4AF37]/30">
                <div className="flex items-start gap-2 text-sm text-[#3B2A1A]/90">
                  <AlertCircle className="w-4 h-4 text-[#B65A14] mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold text-[#3B2A1A]">{lang === 'kn' ? 'ಚೆಕ್-ಇನ್ ಸಮಯದಲ್ಲಿ ಕೌಂಟರ್‌ನಲ್ಲಿ ಅಲ್ಪ ಮೊತ್ತದ, ಮರುಪಾವತಿಸಬಹುದಾದ ಠೇವಣಿಯನ್ನು ಸಂಗ್ರಹಿಸಲಾಗುತ್ತದೆ.' : 'A nominal refundable deposit will be collected at the counter during check-in'}</div>
                    <div className="text-xs text-[#3B2A1A]/80 mt-1">
                      {L(COPY.carryProof)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {/* <button
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-full bg-[#B65A14] text-[#FBF7F0] px-6 py-3 text-sm font-semibold hover:bg-[#8F4610] transition shadow"
                  data-testid="print-btn"
                >
                  <Printer className="w-4 h-4" /> {L(COPY.printConfirmation)}
                </button> */}
                <button
                  onClick={restart}
                  className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 text-[#3B2A1A] px-6 py-3 text-sm font-semibold hover:bg-[#F2E7D5] transition"
                  data-testid="book-again-btn"
                >
                  {L(COPY.anotherBooking)}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </PageLayout>
  );
};

export default RoomBooking;
